/**
 * Absolute Value Inequalities generator.
 *
 * The two core patterns, which students must not confuse:
 *   |expr| < k   ->  AND   (-k < expr < k)   -> bounded interval
 *   |expr| > k   ->  OR    (expr < -k or expr > k) -> union of rays
 * plus the degenerate cases when k <= 0.
 *
 * The interval/union is computed from the inner linear expression a x + b by
 * solving the two boundary inequalities with the independent LinearInequality
 * solver, then verified by membership probes. This guarantees the AND vs OR
 * structure is never emitted backwards.
 *
 * Families:
 *   V1: |x| (op) k
 *   V2: |x + b| (op) k
 *   V3: |ax + b| (op) k
 *   V4: c|ax + b| + d (op) e   (isolate the absolute value first)
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr } from "../engine/linear";
import {
  LinearInequality,
  inSolutionSet,
  solutionSetToAnswer,
  solutionSetLatex,
  type SolutionSet,
} from "../engine/inequality";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "absolute-value-inequalities" as const;
const VERSION = "1.0.0";
const VARS = ["x", "n", "a", "m", "y"] as const;

type Family = "V1" | "V2" | "V3" | "V4";
type LessOp = "<" | "<=";
type GreatOp = ">" | ">=";
type AvOp = LessOp | GreatOp;

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly bMax: number;
  readonly kMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["V1", "V2"], coeffMax: 1, bMax: 6, kMax: 9, pNeg: 0.2 },
  standard: { families: ["V2", "V3"], coeffMax: 4, bMax: 8, kMax: 10, pNeg: 0.4 },
  hard: { families: ["V3", "V4"], coeffMax: 6, bMax: 9, kMax: 12, pNeg: 0.5 },
  challenge: { families: ["V4"], coeffMax: 8, bMax: 10, kMax: 14, pNeg: 0.5 },
};

function factorL(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toLatex();
}
function coeffL(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
function opL(op: AvOp): string {
  return op === "<=" ? "\\le" : op === ">=" ? "\\ge" : op;
}
function isLess(op: AvOp): op is LessOp {
  return op === "<" || op === "<=";
}

interface Built {
  family: Family;
  variable: string;
  displayLatex: string;
  set: SolutionSet;
  connector: "and" | "or" | "special";
}

/** Solve inner (op) bound as a ray. inner = a x + b. */
function ray(a: Rational, b: Rational, op: AvOp, bound: Rational) {
  return new LinearInequality(new LinearExpr(a, b), op, LinearExpr.constantExpr(bound)).solve();
}

function build(rng: Rng, difficulty: Difficulty): Built | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(VARS);
  const family = rng.pick(cfg.families);
  const op: AvOp = rng.pick<AvOp>(["<", "<=", ">", ">="]);

  const a =
    family === "V1" || family === "V2"
      ? Rational.ONE
      : rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * rng.sign(cfg.pNeg));
  if (a.isZero()) return null;
  const b = family === "V1" ? Rational.ZERO : rat(rng.int(1, cfg.bMax) * rng.sign(cfg.pNeg));
  const k = rat(rng.int(1, cfg.kMax)); // positive threshold (k>0 for meaningful problems)

  // |inner| (op) k. Build the set from the two boundary inequalities.
  // For "< k":  inner > -k AND inner < k. For "> k": inner < -k OR inner > k.
  const inner = `${coeffL(a, v)}${b.isZero() ? "" : ` ${plusL(b)}`}`;
  let displayLatex: string;
  if (family === "V4") {
    const c = rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * (rng.bool() ? 1 : -1));
    const d = rat(rng.int(1, cfg.kMax) * rng.sign(cfg.pNeg));
    const e = c.mul(k).add(d);
    displayLatex = `${factorL(c)}\\left|${inner}\\right| ${plusL(d)} ${opL(op)} ${e.toLatex()}`;
  } else {
    displayLatex = `\\left|${inner}\\right| ${opL(op)} ${k.toLatex()}`;
  }

  let set: SolutionSet;
  let connector: "and" | "or";
  if (isLess(op)) {
    // AND interval: -k (op) inner (op) k
    const low = ray(a, b, op === "<=" ? ">=" : ">", k.neg()); // inner >= -k
    const high = ray(a, b, op, k); // inner <= k
    if (low.kind !== "ray" || high.kind !== "ray") return null;
    const lower = [low, high].find((r) => r.op === ">" || r.op === ">=")!;
    const upper = [low, high].find((r) => r.op === "<" || r.op === "<=")!;
    if (!lower || !upper) return null;
    set = {
      kind: "interval",
      lo: lower.value,
      loInclusive: lower.op === ">=",
      hi: upper.value,
      hiInclusive: upper.op === "<=",
    };
    if (set.lo.gte(set.hi)) return null;
    connector = "and";
  } else {
    // OR union: inner (op) -k  OR  inner (op) k
    const low = ray(a, b, op === ">=" ? "<=" : "<", k.neg()); // inner <= -k
    const high = ray(a, b, op, k); // inner >= k
    if (low.kind !== "ray" || high.kind !== "ray") return null;
    const lowPiece = [low, high].find((r) => r.op === "<" || r.op === "<=")!;
    const highPiece = [low, high].find((r) => r.op === ">" || r.op === ">=")!;
    if (!lowPiece || !highPiece) return null;
    if (lowPiece.value.gte(highPiece.value)) return null;
    set = {
      kind: "union",
      lo: lowPiece.value,
      loInclusive: lowPiece.op === "<=",
      hi: highPiece.value,
      hiInclusive: highPiece.op === ">=",
    };
    connector = "or";
  }

  return { family, variable: v, displayLatex, set, connector };
}

/** Independently verify the interval/union by probing. */
function verify(b: Built): boolean {
  if (b.set.kind === "interval") {
    const mid = b.set.lo.add(b.set.hi).div(rat(2));
    return (
      inSolutionSet(b.set, mid) &&
      !inSolutionSet(b.set, b.set.lo.sub(rat(1))) &&
      !inSolutionSet(b.set, b.set.hi.add(rat(1)))
    );
  }
  if (b.set.kind === "union") {
    const mid = b.set.lo.add(b.set.hi).div(rat(2));
    return (
      !inSolutionSet(b.set, mid) &&
      inSolutionSet(b.set, b.set.lo.sub(rat(1))) &&
      inSolutionSet(b.set, b.set.hi.add(rat(1)))
    );
  }
  return false;
}

export class AbsoluteValueInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 300; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const b = build(rng, ctx.difficulty);
      if (!b) continue;
      if (!verify(b)) continue;
      const answer = solutionSetToAnswer(b.set);

      return {
        id: `${TOPIC}-${ctx.seed}-${b.family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: b.family,
        difficulty: ctx.difficulty,
        prompt: { text: b.displayLatex, latex: b.displayLatex },
        answerType: "compound",
        answer,
        steps: [
          {
            description:
              b.connector === "and"
                ? "Less-than absolute value becomes an AND: the expression is between the negative and positive of the value."
                : "Greater-than absolute value becomes an OR: the expression is beyond the value on either side.",
            latex: solutionSetLatex(b.set, b.variable),
          },
        ],
        hints: [
          { level: 1, text: "First isolate the absolute value expression." },
          {
            level: 2,
            text: "|expr| < k means AND (between -k and k); |expr| > k means OR (outside -k and k).",
          },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 300 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const a = q.answer;
    const structure =
      a.kind === "compound"
        ? `${a.connector}:${a.lower.value.key()}:${a.upper.value.key()}`
        : a.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const absoluteValueInequalities = new AbsoluteValueInequalitiesGenerator();
